import { post } from 'axios';
import Card from 'Components/ContractGenerator/layouts/Card';
import CheckMark from 'Components/ContractGenerator/layouts/CheckMark';
import Field from 'Components/ContractGenerator/pages/Step2/ContractData/Form/Field/Field';
import { cloneDeep, has } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { contractGeneratorOperations, contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import Translation from 'ViatoUi/Shared/Utilities/Translation';

import AdditionalFeatures from '../AdditionalFeatures';
import styles from './Extension.module.scss';

const Extension = ({
	translationKey,
	feeDiscountValue,
	feeDiscountType,
	extensionDiscountValue,
	extensionDiscountType,
	isSinglePayment,
	buttonSettings,
	additionalDescriptions,
	infoComponents,
	id,
	extensionOption,
	hasButton,
	selectableExtensionOptions,
	isSummary
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation('translation');
	const urlPrefix = process.env.NODE_ENV !== 'production' ? '' : 'https://contract-generator.viato.travel';
	const { selectedRooms, selectedPaymentType, selectedContractTermInMonths, selectedExtensions } = useSelector(
		contractGeneratorSelectors.getDataToSubmit
	);

	const [extensionPrice, setExtensionPrice] = useState(null);
	const [extensionPriceDiscounted, setExtensionPriceDiscounted] = useState(null);
	const [feePrice, setFeePrice] = useState(null);
	const [feePriceDiscounted, setFeePriceDiscounted] = useState(null);
	const extensionValues = selectedExtensions.selectedExtensionOptions?.[id] || [];

	useEffect(() => {
		setExtensionPrice(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: extensionOption,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: false, isSetupFee: false }
		}).then(response => setExtensionPrice(response.data.price));

		setExtensionPriceDiscounted(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: extensionOption,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: true, isSetupFee: false }
		}).then(response => setExtensionPriceDiscounted(response.data.price));

		setFeePrice(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: extensionOption.setupFee,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: false, isSetupFee: true }
		}).then(response => setFeePrice(response.data.price));

		setFeePriceDiscounted(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: extensionOption.setupFee,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: true, isSetupFee: true }
		}).then(response => setFeePriceDiscounted(response.data.price));
	}, [extensionOption, selectedRooms, selectedPaymentType, selectedContractTermInMonths]);

	return (
		<>
			{id !== null && <div id={`viato-contract-generator-extension-${id}`} />}
			<Card
				buttonSettings={buttonSettings}
				hasButton={hasButton}
				headline={t(`extensions.${translationKey}.title`)}
				priceFooterSettings={{
					feePrice,
					feePriceDiscounted,
					feeDiscountValue,
					feeDiscountType,
					price: extensionPrice,
					priceDiscounted: extensionPriceDiscounted,
					discountValue: extensionDiscountValue,
					discountType: extensionDiscountType,
					isSinglePayment
				}}
				hasPriceFooter
			>
				<FlexBox flexWrap='wrap'>
					{t(`extensions.${translationKey}.infos`).map((infoSet, infoSetIndex) => (
						<div key={`${id}-${infoSet[0]}`} className={styles.checkmarkWrap}>
							{infoSet.map((info, infoIndex) => (
								<CheckMark
									key={`${id}-${info}`}
									text={
										<Translation
											key={info}
											components={infoComponents}
											translationKey={`extensions.${translationKey}.infos.${infoSetIndex}.${infoIndex}`}
											translationNamespace='translation'
										/>
									}
								/>
							))}
						</div>
					))}
				</FlexBox>

				{selectableExtensionOptions.length > 0 &&
					selectableExtensionOptions.map(({ optionGroup, type, options }) => (
						<FlexBox key={optionGroup} className={styles.optionGroup} flexWrap='wrap' justifyContent='space-between'>
							<FlexBox className={styles.optionGroupText}>
								{t(`selectableExtensionOptions.optionGroups.${optionGroup}.title`)}
							</FlexBox>

							{isSummary && (
								<div key={`${id}-wrap-${optionGroup}`} className={styles.checkmarkWrap}>
									{extensionValues.map(valueName => (
										<CheckMark key={`${id}-${valueName}`} text={valueName} />
									))}
								</div>
							)}

							{!isSummary &&
								options.map(optionName => (
									<Field
										key={`${optionGroup}-${optionName}`}
										placeholder={optionName}
										showErrorMessage={false}
										type={type}
										value={extensionValues.includes(optionName)}
										onChange={newValue => {
											let newData = cloneDeep(selectedExtensions);
											let updateExtensions = [...newData.ids];
											let updateValues = [];

											if (newValue) {
												updateValues = has(newData.selectedExtensionOptions, id)
													? [...newData.selectedExtensionOptions[id], optionName]
													: [optionName];

												if (!updateExtensions.includes(id)) {
													updateExtensions = [...updateExtensions, id];
												}
											} else {
												updateValues = newData.selectedExtensionOptions[id].filter(option => option !== optionName);

												if (updateExtensions.length === 1 && updateValues.length === 0) {
													updateExtensions = updateExtensions.filter(item => item !== id);
												}
											}

											newData = {
												...newData,
												ids: [...updateExtensions],
												selectedExtensionOptions: {
													...newData.selectedExtensionOptions,
													[id]: updateValues
												}
											};

											dispatch(contractGeneratorOperations.setDataToSubmit({ selectedExtensions: { ...newData } }));
										}}
										onMount={() => {}}
										onUnmount={() => {}}
									/>
								))}
						</FlexBox>
					))}

				<AdditionalFeatures infos={t(`extensions.${translationKey}.additionalInfos`)} />

				{additionalDescriptions.length > 0 && (
					<>
						{additionalDescriptions.map(({ key, components }) => (
							<Translation
								key={key}
								components={components}
								translationKey={`extensions.${translationKey}.additionalDescriptions.${key}`}
								translationNamespace='translation'
							/>
						))}
					</>
				)}
			</Card>
			<Spacer height='l' />
		</>
	);
};

Extension.propTypes = {
	extensionOption: PropTypes.object.isRequired,
	additionalDescriptions: PropTypes.array.isRequired,
	selectableExtensionOptions: PropTypes.array.isRequired,
	infoComponents: PropTypes.array.isRequired,
	translationKey: PropTypes.string.isRequired,
	feeDiscountValue: PropTypes.number.isRequired,
	feeDiscountType: PropTypes.string.isRequired,
	extensionDiscountValue: PropTypes.number.isRequired,
	extensionDiscountType: PropTypes.string.isRequired,
	id: PropTypes.string,
	isSinglePayment: PropTypes.bool.isRequired,
	buttonSettings: PropTypes.object.isRequired,
	hasButton: PropTypes.bool,
	isSummary: PropTypes.bool
};

Extension.defaultProps = {
	id: null,
	hasButton: true,
	isSummary: false
};

export default Extension;
